import { apiRequest, buildApiUrl, getHeaders } from '../utils/api';

/**
 * Storage and Governance Service
 * Handles organization storage provisioning (Default / BYO Azure Blob Storage)
 * and site collection opaque container provisioning (APIs #8 to #13).
 */

/**
 * Fetches organization storage configuration status, type, and health.
 * API #8: GET /organizations/{orgId}/storage/status
 *
 * @param {string} organizationId 
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   organizationId: string,
 *   type: 'default' | 'byo',
 *   status: 'not_provisioned' | 'ready' | 'credential_invalid' | 'provisioning' | 'migration_pending' | 'blocked' | 'hold_blocked',
 *   accountNameFingerprint: string,
 *   health: 'healthy' | 'unhealthy' | 'degraded',
 *   lastProvisionedAtUtc?: string,
 *   lastValidatedAtUtc?: string,
 *   operationId?: string
 * }>}
 */
export const getStorageStatus = async (organizationId, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/status`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'GET',
    headers: getHeaders(),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Provisions default Cognior-managed storage for the organization (idempotent).
 * API #9: POST /organizations/{orgId}/storage/provision
 *
 * @param {string} organizationId 
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export const provisionDefaultStorage = async (organizationId, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/provision`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({}),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Submits customer Bring-Your-Own (BYO) Azure Storage connection string.
 * Write-only credential submission: never logged or persisted in client-side state.
 * API #10: POST /organizations/{orgId}/storage/byo
 *
 * @param {string} organizationId 
 * @param {string} connectionString 
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export const submitBYOStorage = async (organizationId, connectionString, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!connectionString || typeof connectionString !== 'string') {
    throw new Error('connectionString is required');
  }

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/byo`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ connectionString: connectionString.trim() }),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Re-validates organization storage credentials and updates lastValidatedAtUtc.
 * API #11: POST /organizations/{orgId}/storage/validate
 *
 * @param {string} organizationId 
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export const validateStorage = async (organizationId, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/validate`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({}),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Rotates BYO Azure Storage connection string and updates masked fingerprint.
 * Write-only: never logged or persisted.
 * API #12: POST /organizations/{orgId}/storage/rotate
 *
 * @param {string} organizationId 
 * @param {string} newConnectionString 
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export const rotateStorage = async (organizationId, newConnectionString, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!newConnectionString || typeof newConnectionString !== 'string') {
    throw new Error('newConnectionString is required');
  }

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/rotate`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ newConnectionString: newConnectionString.trim() }),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Creates an isolated opaque blob container (c-...) and opaque origin prefixes for a site collection.
 * API #13: POST /organizations/{orgId}/storage/site-collections/{siteId}/provision-container
 *
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {string[]} [approvedOrigins=[]]
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   organizationId: string,
 *   siteCollectionId: string,
 *   containerName: string,
 *   status: string,
 *   originPrefixes: Array<{ origin: string, opaquePrefix: string }>,
 *   provisionedAtUtc?: string
 * }>}
 */
export const provisionSiteContainer = async (organizationId, siteCollectionId, approvedOrigins = [], { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/site-collections/${encodeURIComponent(siteCollectionId)}/provision-container`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ approvedOrigins }),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Toggles compliance Legal Hold on a site collection.
 * API #21: PUT /organizations/{orgId}/storage/site-collections/{siteId}/legal-hold
 *
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {boolean} legalHoldActive
 * @param {string|null} [reason=null]
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ organizationId: string, siteCollectionId: string, legalHoldActive: boolean, legalHoldReason?: string, updatedAtUtc?: string }>}
 */
export const setSiteLegalHold = async (organizationId, siteCollectionId, legalHoldActive, reason = null, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (typeof legalHoldActive !== 'boolean') {
    throw new Error('legalHoldActive boolean is required');
  }

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/site-collections/${encodeURIComponent(siteCollectionId)}/legal-hold`;
  const body = {
    legalHoldActive,
    ...(reason ? { legalHoldReason: reason } : {})
  };

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(body),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Cleans up all site behavioural data & deletes the Azure Blob container.
 * API #22: DELETE /organizations/{orgId}/storage/site-collections/{siteId}
 *
 * If Legal Hold is active, the backend returns status: "hold_blocked".
 *
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ status: string, message?: string, deletedAtUtc?: string }>}
 */
export const deleteSiteStorage = async (organizationId, siteCollectionId, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const endpoint = `/organizations/${encodeURIComponent(organizationId)}/storage/site-collections/${encodeURIComponent(siteCollectionId)}`;
  try {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'DELETE',
      headers: getHeaders(),
      ...(signal ? { signal } : {})
    });
    return await response.json();
  } catch (err) {
    if (err?.data?.status === 'hold_blocked' || err?.message?.includes('hold_blocked')) {
      return { status: 'hold_blocked', message: err.data?.message || err.message };
    }
    throw err;
  }
};

